// src/Pages/SpecialGramSabhaStatus.jsx
import { useState, useEffect } from "react";
import { Button, Table, Spin, Alert, message, Tag } from "antd";
import { FileExcelOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  getSpecialGramSabhaStatus,
  getDistrict,
  getBlockByDistrict,
} from "../api/User";
import { Select, DatePicker, Row, Col } from "antd";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FilePdfOutlined } from "@ant-design/icons";

const flattenColumns = (cols) => {
  let result = [];

  cols.forEach((col) => {
    if (col.children) {
      result = result.concat(flattenColumns(col.children));
    } else {
      result.push(col);
    }
  });

  return result;
};



const formatValueForExport = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  // Convert to string and escape quotes
  const stringValue = String(value);

  // If value contains quotes, commas, or newlines, wrap in quotes and escape existing quotes
  if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

const formatExportValue = (key, value) => {
  // Boolean style fields (handle first so null/undefined become 'No')
  if (
    key === "is_develop_work" ||
    key === "is_amritsar_work" ||
    key === "drinking_water" ||
    key === "drug_addiction" ||
    key === "cleanliness" ||
    key === "inspection_of_govt_building" ||
    key === "wastewater"
  ) {
    return value === true || value === 1 || value === "1" ? "Yes" : "No";
  }

  if (value === null || value === undefined || value === "") return "";


  // ✅ Add this block
  if (
    key === "meeting_videos" ||
    key === "meeting_images" ||
    key === "proceeding_book"
  ) {
    return value === true || value === 1 || value === "1"
      ? "Uploaded"
      : "Not Uploaded";
  }
  // Quorum status fallback
  if (key === "quorum_status") {
    return value || "Failed";
  }

  return value;
};

const SpecialGramSabhaStatus = ({ dateFilter }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [districtCode, setDistrictCode] = useState(null);
  const [blockCode, setBlockCode] = useState(null);
  const [startDate, setStartDate] = useState(null);

  // District and Block dropdown data
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingBlocks, setLoadingBlocks] = useState(false);

  const [exportLoading, setExportLoading] = useState(false);
  const [roleId, setRoleId] = useState(null);

  // Fetch districts on component mount
  useEffect(() => {
    const role = sessionStorage.getItem("role_id");
    setRoleId(role);
    fetchDistricts();
  }, []);

  // Fetch blocks when district changes
  useEffect(() => {
    if (districtCode) {
      fetchBlocks(districtCode);
    } else {
      setBlocks([]);
    }
  }, [districtCode]);

  // Auto fetch when filter changes
  useEffect(() => {
    setCurrentPage(1);
    fetchReport(1, pageSize);
  }, [districtCode, blockCode, startDate, dateFilter]);

  const fetchDistricts = async () => {
    try {
      setLoadingDistricts(true);
      const token = sessionStorage.getItem("token");
      const userId = sessionStorage.getItem("user_id");
      const role = sessionStorage.getItem("role_id");

      const result = await getDistrict(token, userId, role);

      if (result.status === "success" && result.data) {
        setDistricts(result.data);

        // Auto-select district for BDPO (role 7) and Sarpanch (role 3)
        if ((role === "2" || role === "7" || role === "3") && result.data.length === 1) {
          setDistrictCode(result.data[0].district_code);
        }
      }
    } catch (err) {
      console.error("Error fetching districts:", err);
      message.error("Failed to load districts");
    } finally {
      setLoadingDistricts(false);
    }
  };

  const fetchBlocks = async (selectedDistrictCode) => {
    try {
      setLoadingBlocks(true);
      const token = sessionStorage.getItem("token");
      const userId = sessionStorage.getItem("user_id");
      const role = sessionStorage.getItem("role_id");

      const result = await getBlockByDistrict(
        token,
        selectedDistrictCode,
        userId,
        role
      );

      if (result.status === "success" && result.data) {
        setBlocks(result.data);

        // Auto-select block for BDPO (role 7)
        if (role === "7" && result.data.length === 1) {
          setBlockCode(result.data[0].block_code);
        }
      }
    } catch (err) {
      console.error("Error fetching blocks:", err);
      message.error("Failed to load blocks");
    } finally {
      setLoadingBlocks(false);
    }
  };


  const [overallTotals, setOverallTotals] = useState({
    gram_sabha_number: 0,
    population: 0,
    voter_number: 0,
    gram_sabha_attendance: 0,
    families_income_less_1lakh: 0,
    no_of_objections: 0,
    old_age_samman_allowance: 0,
    old_age_pension_objections: 0,
    disability_pension: 0,
    disability_pension_objections: 0,
    widow_pension: 0,
    widow_pension_objections: 0,
    deen_dayal_yojana: 0,
    deen_dayal_objections: 0,
    drinking_water: 0,
    drug_addiction: 0,
    cleanliness: 0,
    inspection_of_govt_building: 0,
    wastewater: 0,
  });



  const columns = [
    {
      title: "SR.NO.",
      dataIndex: "sr_no",
      key: "sr_no",
      width: 45,
      fixed: "left",
      align: "center",
      render: (text, record, index) => {
        return (currentPage - 1) * pageSize + index + 1;
      },
    },
    {
      title: "District",
      dataIndex: "district",
      key: "district",
      width: 90,
      align: "center",
      sorter: (a, b) => {
        const distA = (a.district || '').toLowerCase();
        const distB = (b.district || '').toLowerCase();
        return distA.localeCompare(distB);
      },
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    },
    {
      title: "Block",
      dataIndex: "block",
      key: "block",
      width: 90,
      align: "center",
      sorter: (a, b) => {
        const blockA = (a.block || '').toLowerCase();
        const blockB = (b.block || '').toLowerCase();
        return blockA.localeCompare(blockB);
      },
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    },
    {
      title: "Gram Panchayat",
      dataIndex: "gram_panchayat",
      key: "gram_panchayat",
      width: 100,
      align: "center",
      sorter: (a, b) => {
        const gpA = (a.gram_panchayat || '').toLowerCase();
        const gpB = (b.gram_panchayat || '').toLowerCase();
        return gpA.localeCompare(gpB);
      },
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    },
    {
      title: "Date of Gram Sabha",
      dataIndex: "date_of_gram_sabha",
      key: "date_of_gram_sabha",
      width: 85,
      align: "center",
      sorter: (a, b) => {
        const dateA = (a.date_of_gram_sabha || '').split('/').reverse().join('');
        const dateB = (b.date_of_gram_sabha || '').split('/').reverse().join('');
        return dateA.localeCompare(dateB);
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: "GS No.",
      dataIndex: "gram_sabha_number",
      key: "gram_sabha_number",
      width: 60,
      align: "center",
      sorter: (a, b) => {
        const numA = parseInt(a.gram_sabha_number) || 0;
        const numB = parseInt(b.gram_sabha_number) || 0;
        return numA - numB;
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: "Population (PPP)",
      dataIndex: "population",
      key: "population",
      width: 70,
      align: "center",
      sorter: (a, b) => (Number(a.population) || 0) - (Number(b.population) || 0),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: "Voters",
      dataIndex: "voter_number",
      key: "voter_number",
      width: 65,
      align: "center",
      sorter: (a, b) => (Number(a.voter_number) || 0) - (Number(b.voter_number) || 0),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: "GS Attendance",
      dataIndex: "gram_sabha_attendance",
      key: "gram_sabha_attendance",
      width: 75,
      align: "center",
      sorter: (a, b) => (Number(a.gram_sabha_attendance) || 0) - (Number(b.gram_sabha_attendance) || 0),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: "Meeting Videos",
      dataIndex: "meeting_videos",
      key: "meeting_videos",
      width: 150,
      align: "center",
      sorter: (a, b) => (Number(a.meeting_videos) || 0) - (Number(b.meeting_videos) || 0),
      render: (val) => (
        <Tag color={val ? "green" : "red"} style={{ fontSize: "10px" }}>
          {val ? "Uploaded" : "Not Uploaded"}
        </Tag>
      ),
    },

    {
      title: "Meeting Images",
      dataIndex: "meeting_images",
      key: "meeting_images",
      width: 150,
      align: "center",
      sorter: (a, b) => (Number(a.meeting_images) || 0) - (Number(b.meeting_images) || 0),
      render: (val) => (
        <Tag color={val ? "green" : "red"} style={{ fontSize: "10px" }}>
          {val ? "Uploaded" : "Not Uploaded"}
        </Tag>
      ),
    },

    {
      title: "Proceeding Book",
      dataIndex: "proceeding_book",
      key: "proceeding_book",
      width: 150,
      align: "center",
      sorter: (a, b) => (Number(a.proceeding_book) || 0) - (Number(b.proceeding_book) || 0),
      render: (val) => (
        <Tag color={val ? "green" : "red"} style={{ fontSize: "10px" }}>
          {val ? "Uploaded" : "Not Uploaded"}
        </Tag>
      ),
    },
    {
      title: "Quorum Status",
      dataIndex: "quorum_status",
      key: "quorum_status",
      width: 85,
      align: "center",
      sorter: (a, b) => {
        const statusA = (a.quorum_status || '').toLowerCase();
        const statusB = (b.quorum_status || '').toLowerCase();
        return statusA.localeCompare(statusB);
      },
      sortDirections: ['ascend', 'descend'],
      render: (val) => {
        const status = val || "Failed"; // fallback for null/undefined/empty

        return (
          <Tag
            color={status === "Fulfilled" ? "green" : "red"}
            style={{ fontSize: "10px" }}
          >
            {status}
          </Tag>
        );
      },
    },
    ...(dateFilter === "on" ? [
      {
        title: "Families (<1L)",
        dataIndex: "families_income_less_1lakh",
        key: "families_income_less_1lakh",
        width: 75,
        align: "center",
        sorter: (a, b) => (Number(a.families_income_less_1lakh) || 0) - (Number(b.families_income_less_1lakh) || 0),
      },
      {
        title: "Total Objections",
        dataIndex: "no_of_objections",
        key: "no_of_objections",
        width: 70,
        align: "center",
        sorter: (a, b) => (Number(a.no_of_objections) || 0) - (Number(b.no_of_objections) || 0),
      }
    ] : []),

    {
      title: "Development Work",
      dataIndex: "is_develop_work",
      key: "is_develop_work",
      width: 100,
      align: "center",
      sorter: (a, b) => (a.is_develop_work === b.is_develop_work ? 0 : a.is_develop_work ? -1 : 1),
      render: (val) => (
        <Tag
          color={val ? "green" : "red"}
          style={{ fontSize: "10px" }}
        >
          {val ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Amrit Sarovar",
      dataIndex: "is_amritsar_work",
      key: "is_amritsar_work",
      width: 100,
      align: "center",
      sorter: (a, b) => (a.is_amritsar_work === b.is_amritsar_work ? 0 : a.is_amritsar_work ? -1 : 1),
      render: (val) => (
        <Tag
          color={val ? "green" : "red"}
          style={{ fontSize: "10px" }}
        >
          {val ? "Yes" : "No"}
        </Tag>
      ),
    },
    ...(dateFilter === "on" ? [
      {
        title: "No of members of schemes",
        children: [
          {
            title: "Old Age Samman Allowance",
            dataIndex: "old_age_samman_allowance",
            key: "old_age_samman_allowance",
            width: 80,
            align: "center",
            sorter: (a, b) => (Number(a.old_age_samman_allowance) || 0) - (Number(b.old_age_samman_allowance) || 0),
          },
          {
            title: "No of Objections",
            dataIndex: "old_age_pension_objections",
            key: "old_age_pension_objections",
            width: 70,
            align: "center",
            sorter: (a, b) => (Number(a.old_age_pension_objections) || 0) - (Number(b.old_age_pension_objections) || 0),
          },
          {
            title: "Disability Pension",
            dataIndex: "disability_pension",
            key: "disability_pension",
            width: 80,
            align: "center",
            sorter: (a, b) => (Number(a.disability_pension) || 0) - (Number(b.disability_pension) || 0),
          },
          {
            title: "No of Objections",
            dataIndex: "disability_pension_objections",
            key: "disability_pension_objections",
            width: 70,
            align: "center",
            sorter: (a, b) => (Number(a.disability_pension_objections) || 0) - (Number(b.disability_pension_objections) || 0),
          },
          {
            title: "Widow Pension",
            dataIndex: "widow_pension",
            key: "widow_pension",
            width: 80,
            align: "center",
            sorter: (a, b) => (Number(a.widow_pension) || 0) - (Number(b.widow_pension) || 0),
          },
          {
            title: "No of Objections",
            dataIndex: "widow_pension_objections",
            key: "widow_pension_objections",
            width: 70,
            align: "center",
            sorter: (a, b) => (Number(a.widow_pension_objections) || 0) - (Number(b.widow_pension_objections) || 0),
          },
          {
            title: "Deen Dayal Lado Lakshmi Yojana 2025",
            dataIndex: "deen_dayal_yojana",
            key: "deen_dayal_yojana",
            width: 100,
            align: "center",
            sorter: (a, b) => (Number(a.deen_dayal_yojana) || 0) - (Number(b.deen_dayal_yojana) || 0),
          },
          {
            title: "No of Objections",
            dataIndex: "deen_dayal_objections",
            key: "deen_dayal_objections",
            width: 70,
            align: "center",
            sorter: (a, b) => (Number(a.deen_dayal_objections) || 0) - (Number(b.deen_dayal_objections) || 0),
          },
        ],
      }
    ] : []),
    ...(dateFilter === "after" ? [
      {
        title: "Village Drinking Water Supply Status",
        dataIndex: "drinking_water",
        key: "drinking_water",
        width: 130,
        align: "center",
        sorter: (a, b) => (Number(a.drinking_water) || 0) - (Number(b.drinking_water) || 0),
        render: (val) => (
          <Tag color={val ? "green" : "red"} style={{ fontSize: "10px" }}>
            {val ? "Yes" : "No"}
          </Tag>
        ),
      },
      {
        title: "Status of Drug Addiction",
        dataIndex: "drug_addiction",
        key: "drug_addiction",
        width: 130,
        align: "center",
        sorter: (a, b) => (Number(a.drug_addiction) || 0) - (Number(b.drug_addiction) || 0),
        render: (val) => (
          <Tag color={val ? "green" : "red"} style={{ fontSize: "10px" }}>
            {val ? "Yes" : "No"}
          </Tag>
        ),
      },
      {
        title: "Cleanliness, Solid Waste Management",
        dataIndex: "cleanliness",
        key: "cleanliness",
        width: 150,
        align: "center",
        sorter: (a, b) => (Number(a.cleanliness) || 0) - (Number(b.cleanliness) || 0),
        render: (val) => (
          <Tag color={val ? "green" : "red"} style={{ fontSize: "10px" }}>
            {val ? "Yes" : "No"}
          </Tag>
        ),
      },
      {
        title: "Inspection of Government Building",
        dataIndex: "inspection_of_govt_building",
        key: "inspection_of_govt_building",
        width: 150,
        align: "center",
        sorter: (a, b) => (Number(a.inspection_of_govt_building) || 0) - (Number(b.inspection_of_govt_building) || 0),
        render: (val) => (
          <Tag color={val ? "green" : "red"} style={{ fontSize: "10px" }}>
            {val ? "Yes" : "No"}
          </Tag>
        ),
      },
      {
        title: "Wastewater Management",
        dataIndex: "wastewater",
        key: "wastewater",
        width: 130,
        align: "center",
        sorter: (a, b) => (Number(a.wastewater) || 0) - (Number(b.wastewater) || 0),
        render: (val) => (
          <Tag color={val ? "green" : "red"} style={{ fontSize: "10px" }}>
            {val ? "Yes" : "No"}
          </Tag>
        ),
      }
    ] : []),

  ];

  const handleExportPDF = async () => {
    try {
      if (exportLoading || !totalRecords) {
        message.warning("No data available to export");
        return;
      }

      message.loading({
        content: "Preparing PDF export...",
        key: "exportPdf",
        duration: 0,
      });

      // Fetch ALL data
      const allData = await fetchAllDataForExport();

      if (!allData.length) {
        message.destroy("exportPdf");
        message.warning("No data to export");
        return;
      }

      // Create PDF document
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = doc.internal.pageSize.getWidth();

      // Title
      doc.setFontSize(14);
      doc.text("Special Gram Sabha Status", pageWidth / 2, 12, {
        align: "center",
      });

      // Date and filter info
      doc.setFontSize(9);
      let filterText = "Generated on: " + new Date().toLocaleString();

      if (districtCode || blockCode || startDate) {
        filterText += " | Filters: ";
        if (districtCode) {
          const district = districts.find(
            (d) => d.district_code === districtCode
          );
          filterText += `District: ${district?.district || districtCode} `;
        }
        if (blockCode) {
          const block = blocks.find((b) => b.block_code === blockCode);
          filterText += `Block: ${block?.block || blockCode} `;
        }
        if (startDate) {
          filterText += `Date: ${startDate.format("DD/MM/YYYY")}`;
        }
      }

      doc.text(filterText, pageWidth / 2, 18, { align: "center" });

      // Flatten columns (handles children)
      const flatColumns = flattenColumns(columns);

      // Headers
      const headers = flatColumns.map((col) => col.title);

      // Body with ALL data - reset serial numbers
      const body = allData.map((row, index) =>
        flatColumns.map((col) => {
          if (col.dataIndex === "sr_no") return index + 1;

          return formatExportValue(col.dataIndex, row[col.dataIndex]);
        })
      );

      const totalRowPdf = flatColumns.map((col, index) => {
        if (index === 0) return "GRAND TOTAL";
        if (col.dataIndex === "population") return overallTotals.population;
        if (col.dataIndex === "voter_number") return overallTotals.voter_number;
        if (col.dataIndex === "gram_sabha_attendance") return overallTotals.gram_sabha_attendance;
        if (col.dataIndex === "families_income_less_1lakh") return overallTotals.families_income_less_1lakh;
        if (col.dataIndex === "no_of_objections") return overallTotals.no_of_objections;
        
        if (col.dataIndex === "old_age_samman_allowance") return overallTotals.old_age_samman_allowance;
        if (col.dataIndex === "old_age_pension_objections") return overallTotals.old_age_pension_objections;
        if (col.dataIndex === "disability_pension") return overallTotals.disability_pension;
        if (col.dataIndex === "disability_pension_objections") return overallTotals.disability_pension_objections;
        if (col.dataIndex === "widow_pension") return overallTotals.widow_pension;
        if (col.dataIndex === "widow_pension_objections") return overallTotals.widow_pension_objections;
        if (col.dataIndex === "deen_dayal_yojana") return overallTotals.deen_dayal_yojana;
        if (col.dataIndex === "deen_dayal_objections") return overallTotals.deen_dayal_objections;

        if (col.dataIndex === "drinking_water") return overallTotals.drinking_water || "";
        if (col.dataIndex === "drug_addiction") return overallTotals.drug_addiction || "";
        if (col.dataIndex === "cleanliness") return overallTotals.cleanliness || "";
        if (col.dataIndex === "inspection_of_govt_building") return overallTotals.inspection_of_govt_building || "";
        if (col.dataIndex === "wastewater") return overallTotals.wastewater || "";

        return "";
      });

      body.push(totalRowPdf);


      // Create table
      autoTable(doc, {
        startY: 24,
        head: [headers],
        body,
        theme: "grid",
        styles: {
          fontSize: 6,
          cellPadding: 1.5,
          halign: "center",
          valign: "middle",
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [22, 119, 255],
          textColor: 255,
          fontStyle: "bold",
        },
        margin: { left: 5, right: 5 },
        tableWidth: "auto",
      });

      // Save the PDF
      doc.save(`Special_Gram_Sabha_Status_${Date.now()}.pdf`);

      message.destroy("exportPdf");
      message.success(`PDF exported successfully with ${allData.length} records`);
    } catch (err) {
      console.error("PDF Export Error:", err);
      message.destroy("exportPdf");
      message.error("Failed to export PDF");
    }
  };

  const handleExportExcel = async () => {
    try {
      if (exportLoading || !totalRecords) {
        message.warning("No data available to export");
        return;
      }

      message.loading({
        content: "Preparing Excel export...",
        key: "exportExcel",
        duration: 0,
      });

      // Fetch ALL data
      const allData = await fetchAllDataForExport();

      if (!allData.length) {
        message.destroy("exportExcel");
        message.warning("No data to export");
        return;
      }

      // Prepare columns structure
      const flatColumns = [];
      const groupHeaderRow = [];
      const columnHeaderRow = [];

      columns.forEach((col) => {
        if (col.children && col.children.length) {
          // Parent header
          groupHeaderRow.push(`"${col.title}"`);

          // Empty cells for merged effect
          for (let i = 1; i < col.children.length; i++) {
            groupHeaderRow.push(`""`);
          }

          // Child columns
          col.children.forEach((child) => {
            flatColumns.push(child);
            columnHeaderRow.push(`"${child.title}"`);
          });
        } else {
          groupHeaderRow.push(`""`);
          flatColumns.push(col);
          columnHeaderRow.push(`"${col.title}"`);
        }
      });

      // Create data rows with ALL data
      const dataRows = allData.map((row, index) =>
        flatColumns
          .map((col) => {
            if (col.dataIndex === "sr_no") return `"${index + 1}"`;

            const formatted = formatExportValue(col.dataIndex, row[col.dataIndex]);
            return `"${formatted}"`;
          })
          .join(",")
      );


      // 👉 ADD TOTAL ROW HERE
      const totalRow = flatColumns.map((col, index) => {
        if (index === 0) return `"GRAND TOTAL"`; // First column label
        if (col.dataIndex === "population") return `"${overallTotals.population}"`;
        if (col.dataIndex === "voter_number") return `"${overallTotals.voter_number}"`;
        if (col.dataIndex === "gram_sabha_attendance") return `"${overallTotals.gram_sabha_attendance}"`;
        if (col.dataIndex === "families_income_less_1lakh") return `"${overallTotals.families_income_less_1lakh}"`;
        if (col.dataIndex === "no_of_objections") return `"${overallTotals.no_of_objections}"`;

        if (col.dataIndex === "old_age_samman_allowance") return `"${overallTotals.old_age_samman_allowance}"`;
        if (col.dataIndex === "old_age_pension_objections") return `"${overallTotals.old_age_pension_objections}"`;
        if (col.dataIndex === "disability_pension") return `"${overallTotals.disability_pension}"`;
        if (col.dataIndex === "disability_pension_objections") return `"${overallTotals.disability_pension_objections}"`;
        if (col.dataIndex === "widow_pension") return `"${overallTotals.widow_pension}"`;
        if (col.dataIndex === "widow_pension_objections") return `"${overallTotals.widow_pension_objections}"`;
        if (col.dataIndex === "deen_dayal_yojana") return `"${overallTotals.deen_dayal_yojana}"`;
        if (col.dataIndex === "deen_dayal_objections") return `"${overallTotals.deen_dayal_objections}"`;

        if (col.dataIndex === "drinking_water") return `"${overallTotals.drinking_water || ""}"`;
        if (col.dataIndex === "drug_addiction") return `"${overallTotals.drug_addiction || ""}"`;
        if (col.dataIndex === "cleanliness") return `"${overallTotals.cleanliness || ""}"`;
        if (col.dataIndex === "inspection_of_govt_building") return `"${overallTotals.inspection_of_govt_building || ""}"`;
        if (col.dataIndex === "wastewater") return `"${overallTotals.wastewater || ""}"`;

        return `""`;
      });

      dataRows.push(totalRow.join(","));



      // Combine headers and data
      const csvContent = [
        groupHeaderRow.join(","),
        columnHeaderRow.join(","),
        ...dataRows,
      ].join("\n");

      // Create and download CSV file
      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute(
        "download",
        `Special_Gram_Sabha_Status_${new Date().toISOString().split("T")[0]}.csv`
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.destroy("exportExcel");
      message.success(`Report exported successfully with ${allData.length} records!`);
    } catch (err) {
      console.error("Excel Export Error:", err);
      message.destroy("exportExcel");
      message.error("Failed to export Excel");
    }
  };

  const fetchReport = async (page, limit) => {
    try {
      setLoading(true);
      setError(null);

      const token = sessionStorage.getItem("token");
      const userId = sessionStorage.getItem("user_id");
      const role = sessionStorage.getItem("role_id");

      const payload = {
        page,
        limit,
        user_id: userId,
        role_id: role,
        district_code: districtCode || undefined,
        block_code: blockCode || undefined,
        start_date: startDate ? startDate.format("YYYY-MM-DD") : undefined,
        period: dateFilter || undefined,
      };

      // Remove undefined values
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      console.log("FILTER PAYLOAD:", payload);

      const result = await getSpecialGramSabhaStatus(token, payload);

      if (!result.success) {
        setError(result.message || "Unable to load data");
        return;
      }

      let filteredData = result.data || [];

      const formatted = filteredData.map((row, index) => ({
        key: index + 1,
        sr_no: (page - 1) * limit + index + 1,
        ...row,
      }));

      setData(formatted);
      setTotalRecords(result.pagination?.total || formatted.length);
      if (result.totals) {
        setOverallTotals(result.totals);
      }
    } catch (err) {
      console.error("Fetch Report Error:", err);
      setError("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };
  const fetchAllPagesTotals = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const userId = sessionStorage.getItem("user_id");
      const role = sessionStorage.getItem("role_id");

      let page = 1;
      const limit = 500;
      let hasNext = true;

      let totals = {
        population: 0,
        voter_number: 0,
        gram_sabha_attendance: 0,
        families_income_less_1lakh: 0,
        no_of_objections: 0,
        old_age_samman_allowance: 0,
        old_age_pension_objections: 0,
        disability_pension: 0,
        disability_pension_objections: 0,
        widow_pension: 0,
        widow_pension_objections: 0,
        deen_dayal_yojana: 0,
        deen_dayal_objections: 0,
      };

      while (hasNext) {
        const payload = {
          page,
          limit,
          user_id: userId,
          role_id: role,
          district_code: districtCode || undefined,
          block_code: blockCode || undefined,
          start_date: startDate ? startDate.format("YYYY-MM-DD") : undefined,
          period: dateFilter || undefined,
        };

        Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

        const result = await getSpecialGramSabhaStatus(token, payload);

        let filteredData = result.data || [];

        filteredData.forEach(row => {

          totals.population += Number(row.population || 0);
          totals.voter_number += Number(row.voter_number || 0);
          totals.gram_sabha_attendance += Number(row.gram_sabha_attendance || 0);
          totals.families_income_less_1lakh += Number(row.families_income_less_1lakh || 0);
          totals.no_of_objections += Number(row.no_of_objections || 0);

          totals.old_age_samman_allowance += Number(row.old_age_samman_allowance || 0);
          totals.old_age_pension_objections += Number(row.old_age_pension_objections || 0);
          totals.disability_pension += Number(row.disability_pension || 0);
          totals.disability_pension_objections += Number(row.disability_pension_objections || 0);
          totals.widow_pension += Number(row.widow_pension || 0);
          totals.widow_pension_objections += Number(row.widow_pension_objections || 0);
          totals.deen_dayal_yojana += Number(row.deen_dayal_yojana || 0);
          totals.deen_dayal_objections += Number(row.deen_dayal_objections || 0);
        });


        hasNext = result.pagination?.has_next || (page * limit < (result.pagination?.total || 0));
        page++;
      }

      setOverallTotals(totals);
    } catch (err) {
      console.error("Grand total fetch failed:", err);
    }
  };


  // Add this function after your fetchReport function
  const fetchAllDataForExport = async () => {
    try {
      setExportLoading(true);
      const token = sessionStorage.getItem("token");
      const userId = sessionStorage.getItem("user_id");
      const role = sessionStorage.getItem("role_id");

      // First, get the total count with a small limit
      const initialPayload = {
        page: 1,
        limit: 10, // Small limit to get quick response with total count
        user_id: userId,
        role_id: role,
        district_code: districtCode || undefined,
        block_code: blockCode || undefined,
        start_date: startDate ? startDate.format("YYYY-MM-DD") : undefined,
        period: dateFilter || undefined,
      };

      // Remove undefined values
      Object.keys(initialPayload).forEach((key) => {
        if (initialPayload[key] === undefined) {
          delete initialPayload[key];
        }
      });

      // Get initial data to know total records
      const initialResult = await getSpecialGramSabhaStatus(token, initialPayload);

      if (!initialResult.success || !initialResult.data || initialResult.data.length === 0) {
        message.warning("No data available to export");
        return [];
      }

      const totalRecords = initialResult.pagination?.total || 0;

      // If total records are less than or equal to 1000, fetch all at once
      if (totalRecords <= 1000) {
        const allDataPayload = {
          ...initialPayload,
          page: 1,
          limit: totalRecords, // Fetch all records at once
        };

        const allDataResult = await getSpecialGramSabhaStatus(token, allDataPayload);

        if (allDataResult.success && allDataResult.data) {
          console.log(`Fetched all ${allDataResult.data.length} records at once`);
          let exportData = allDataResult.data;
          return exportData;
        }
      }

      // If more than 1000 records, fetch in batches
      const allData = [];
      const batchSize = 1000; // Adjust based on your API limits
      const totalBatches = Math.ceil(totalRecords / batchSize);

      message.loading({
        content: `Fetching data (0/${totalRecords})...`,
        key: 'fetchProgress',
        duration: 0,
      });

      // Fetch data in batches
      for (let page = 1; page <= totalBatches; page++) {
        const batchPayload = {
          ...initialPayload,
          page: page,
          limit: batchSize,
        };

        const batchResult = await getSpecialGramSabhaStatus(token, batchPayload);

        if (batchResult.success && batchResult.data) {
          allData.push(...batchResult.data);

          // Update progress
          message.loading({
            content: `Fetching data (${allData.length}/${totalRecords})...`,
            key: 'fetchProgress',
            duration: 0,
          });
        }

        // Add a small delay to prevent overwhelming the server
        if (page < totalBatches) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      message.destroy('fetchProgress');

      console.log(`Total records fetched: ${allData.length}`);
      let exportData = allData;
      return exportData;
    } catch (err) {
      console.error("Fetch All Data Error:", err);
      message.destroy('fetchProgress');
      message.error("Failed to fetch data for export");
      return [];
    } finally {
      setExportLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const { current, pageSize: newPageSize } = pagination;

    // Only fetch new data if pagination changed (not sorting/filtering)
    if (current !== currentPage || newPageSize !== pageSize) {
      setCurrentPage(current);
      setPageSize(newPageSize);
      fetchReport(current, newPageSize);
    }
    // Sorting and filtering are handled client-side by Ant Design Table
  };

  const handleClearFilters = () => {
    setDistrictCode(null);
    setBlockCode(null);
    setStartDate(null);
    setCurrentPage(1);

    // Fetch data without filters
    fetchReport(1, pageSize);
  };




  return (
    <div className="report-container">
      <div className="report-card">
        <div className="report-header">
          <div>
            <h2 className="report-title">
              Special Gram Sabha Status
              {roleId === "8" && " (CEO-ZP)"}
              {roleId === "7" && " (BDPO)"}
              {roleId === "3" && " (Sarpanch)"}
              {roleId === "5" && " (SEWA)"}
              {roleId === "6" && " (HPPA)"}
              {roleId === "1" && " (Admin)"}
              {roleId === "2" && " (Gram Sachiv)"}
            </h2>
            <p className="report-subtitle">
              Total Records: <strong>{totalRecords}</strong>
            </p>
          </div>


          <div className="report-actions">
            <Button
              onClick={handleExportPDF}
              icon={<FilePdfOutlined />}
              disabled={!data.length || exportLoading}
              loading={exportLoading}
            >
              Export PDF
            </Button>

            <Button
              type="primary"
              onClick={handleExportExcel}
              icon={<FileExcelOutlined />}

              disabled={!data.length || exportLoading}
              loading={exportLoading}
            >
              Export Excel
            </Button>

            <Button
              onClick={() => fetchReport(currentPage, pageSize)}
              icon={<ReloadOutlined />}

              disabled={exportLoading}
            >
              Refresh
            </Button>
          </div>
        </div>

        <Row gutter={12} style={{ marginBottom: 16 }}>
          {/* Show District filter for CEO (8) and Admin (1) */}
          {(roleId === "8" || roleId === "1" || roleId === "5" || roleId === "6" || roleId === "7" || roleId === "2") && (
            <Col xs={24} sm={6}>
              <Select
                allowClear
                placeholder="Select District"
                style={{ width: "100%" }}
                value={districtCode}
                loading={loadingDistricts}
                onChange={(val) => {
                  setDistrictCode(val);
                  setBlockCode(null);
                }}
              >
                {districts.map((d) => (
                  <Select.Option key={d.district_code} value={d.district_code}>
                    {d.district}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          )}

          {/* Show Block filter for CEO (8), BDPO (7), and Admin (1) */}
          {(roleId === "8" || roleId === "7" || roleId === "1" || roleId === "5" || roleId === "6" || roleId === "2") && (
            <Col xs={24} sm={6}>
              <Select
                allowClear
                placeholder="Select Block"
                style={{ width: "100%" }}
                value={blockCode}
                loading={loadingBlocks}
                disabled={!districtCode && (roleId === "8" || roleId === "1" || roleId === "5" || roleId === "6" || roleId === "2")} // Disable if no district selected for CEO/Admin/SEWA/HPPA
                onChange={(val) => setBlockCode(val)}
              >
                {blocks.map((b) => (
                  <Select.Option key={b.block_code} value={b.block_code}>
                    {b.block}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          )}

          {/* Date filter available for all roles */}
          <Col xs={24} sm={6}>
            <DatePicker
              allowClear
              style={{ width: "100%" }}
              placeholder="Select Gram Sabha Date"
              value={startDate}
              onChange={(val) => setStartDate(val)}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Button
              danger
              block
              onClick={handleClearFilters}
              disabled={!districtCode && !blockCode && !startDate}
            >
              Clear All Filters
            </Button>
          </Col>

        </Row>

        {error && (
          <Alert
            type="error"
            message="Error loading data"
            description={error}
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        {loading ? (
          <div className="loading-container">
            <Spin size="large" tip="Loading report..." />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: currentPage,
              pageSize,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              total: totalRecords,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} records`,
            }}
            onChange={handleTableChange}
            bordered
            scroll={{ x: 2000, y: 420 }}
            size="small"
          />
        )}
        <div
          style={{
            backgroundColor: "#e6f7ff",
            border: "1px solid #91d5ff",
            borderRadius: "4px",
            padding: "8px 12px",
            marginTop: "8px",
            display: "flex",
            flexWrap: "wrap",
            gap: "14px",
            fontSize: "11px",
          }}
        >
          <span><strong>Totals :</strong></span>

          {Object.keys(overallTotals).map((key) => {
            // Fields to only show when 'on' is selected
            const onOnlyKeys = [
              "families_income_less_1lakh", "no_of_objections",
              "old_age_samman_allowance", "old_age_pension_objections",
              "disability_pension", "disability_pension_objections",
              "widow_pension", "widow_pension_objections",
              "deen_dayal_yojana", "deen_dayal_objections"
            ];
            
            // Fields to only show when 'after' is selected
            const afterOnlyKeys = [
              "drinking_water", "drug_addiction", "cleanliness",
              "inspection_of_govt_building", "wastewater"
            ];

            if (dateFilter !== "on" && onOnlyKeys.includes(key)) return null;
            if (dateFilter !== "after" && afterOnlyKeys.includes(key)) return null;

            return (
              <span key={key}>
                {key.replace(/_/g, " ").toUpperCase()}:
                <strong> {(overallTotals[key] || 0).toLocaleString()}</strong>
              </span>
            );
          })}

        </div>

      </div>
    </div>
  );
};

export default SpecialGramSabhaStatus;
